<template>
    <tailwind-layout title="Lease Information">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Lease Information</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('properties.show', property.id) + '?tab=rent-payments'">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                    <button v-if="property.rent_info" tabindex="-1" @click="showLeaseModal = true;" type="button" class="mt-1 sm:ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-900 bg-gold-500 hover:bg-gold-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Renew Previous Lease Term
                    </button>
                </div>
            </div>
        </template>

        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="store" class="space-y-8 divide-y divide-gray-200">
                <div class="space-y-8 divide-y divide-gray-200">
                    <div>
                        <div>
                            <h1 class="text-2xl font-bold">{{ property.address_1 }}</h1>
                            <p class="text-sm font-medium text-gray-800 underline">
                                <a :href="'https://www.google.com/maps?q='+property.map_address"
                                   target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5 text-gray-400" aria-hidden="true" /></a>
                            </p>
                        </div>
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="tenant_email"
                                       v-bind:class="(form.errors.tenant_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Tenant Email<span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.tenant_email)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faEnvelope" class="text-gray-400" />
                                    </div>
                                    <input id="tenant_email" v-model="form.tenant_email" type="text" data-demo-target="tenant-email"
                                           v-bind:class="(form.errors.tenant_email)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                                    >
                                    <p v-text="form.errors.tenant_email"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="lease_start_date"
                                       v-bind:class="(form.errors.lease_start_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Start Lease <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.lease_start_date)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                                    </div>
                                    <input id="lease_start_date" v-model="form.lease_start_date" type="date" data-demo-target="lease-start-date"
                                           v-bind:class="(form.errors.lease_start_date)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                                    >
                                    <p v-text="form.errors.lease_start_date"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="lease_end"
                                       v-bind:class="(form.errors.lease_end)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    End Lease <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.lease_end)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                                    </div>
                                    <input id="lease_end" v-model="form.lease_end" type="date" data-demo-target="lease-end-date"
                                           v-bind:class="(form.errors.lease_end)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <p v-text="form.errors.lease_end" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="move_in_date"
                                       v-bind:class="(form.errors.move_in_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Move In Date <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.move_in_date)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                                    </div>
                                    <input id="move_in_date" v-model="form.move_in_date" type="date" data-demo-target="move-in-date"
                                           v-bind:class="(form.errors.move_in_date)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <p v-text="form.errors.move_in_date"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="lease_start"
                                       v-bind:class="(form.errors.lease_start)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Rent Starts From <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.lease_start)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faCalendar" class="text-gray-400" />
                                    </div>
                                    <input id="lease_start" v-model="form.lease_start" type="date" data-demo-target="rent-starts-from"
                                           v-bind:class="(form.errors.lease_start)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <p v-text="form.errors.lease_start"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="rent"
                                       v-bind:class="(form.errors.rent)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Rent <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 mt-2 pointer-events-none"
                                        v-bind:class="(form.errors.rent)?'':'inset-y-0'">
                                        <font-awesome-icon :icon="faDollar" class="text-gray-400" />
                                    </div>
                                    <input id="rent" v-model="form.rent" type="number" step="0.01" data-demo-target="monthly-rent"
                                           v-bind:class="(form.errors.rent)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <small class="text-gray-500 text-sm mt-1 block">
                                        The amount should not be less than $200.00.
                                    </small>
                                    <p v-text="form.errors.rent"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="rent_due_date"
                                       v-bind:class="(form.errors.rent_due_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Day of Month Rent is Due <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <select id="rent_due_date" v-model="form.rent_due_date" data-demo-target="rent-due-date"
                                            v-bind:class="(form.errors.rent_due_date)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'"
                                    >
                                        <option v-for="day in dayOfMonthOptions" :key="'due-'+day" :value="day">{{ day }}</option>
                                    </select>
                                    <p v-text="form.errors.rent_due_date"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="rent_late_on"
                                       v-bind:class="(form.errors.rent_late_on)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Rent is considered late after this day of the month <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <select id="rent_late_on" v-model="form.rent_late_on" data-demo-target="rent-late-on-date"
                                            v-bind:class="(form.errors.rent_late_on)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'"
                                    >
                                        <option v-for="day in dayOfMonthOptions" :key="'late-'+day" :value="day">{{ day }}</option>
                                    </select>
                                    <p v-text="form.errors.rent_late_on"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="late_charges_type"
                                       v-bind:class="(form.errors.late_charges_type)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Late Charges Type
                                </label>
                                <div class="mt-1">
                                    <select id='late_charges_type' v-model="form.late_charges_type" data-demo-target="late-charges-type"
                                            v-bind:class="(form.errors.late_charges_type)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'"
                                    >
                                        <option value="">None</option>
                                        <option v-for="(late_charges_type, key) in late_charges_types" :value="key"
                                                :key="key">{{ late_charges_type }}
                                        </option>
                                    </select>
                                    <p v-text="form.errors.late_charges_type"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="late_charges_amount_or_percentage"
                                       v-bind:class="(form.errors.late_charges_amount_or_percentage)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Amount/Percentage
                                </label>
                                <div class="mt-1">
                                    <select id='late_charges_amount_or_percentage' v-model="form.late_charges_amount_or_percentage" data-demo-target="late-charges-mode"
                                            v-bind:class="(form.errors.late_charges_amount_or_percentage)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'"
                                    >
                                        <option value="">None</option>
                                        <option v-for="(late_charges_amount_or_percentage, key) in amount_or_percentage" :value="key"
                                                :key="key">{{ late_charges_amount_or_percentage }}
                                        </option>
                                    </select>
                                    <p v-text="form.errors.late_charges_amount_or_percentage"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="late_charges_fee"
                                       v-bind:class="(form.errors.late_charges_fee)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Late Charges Fee
                                </label>

                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.late_charges_fee)?'top-3':'inset-y-0'">
                                        <span class="text-gray-400">{{ form.late_charges_amount_or_percentage==2 ? '%' : '$' }}</span>
                                    </div>
                                    <input id="late_charges_fee" v-model="form.late_charges_fee" type="number" step="0.01" data-demo-target="late-charges-fee"
                                           v-bind:class="(form.errors.late_charges_fee)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <p v-text="form.errors.late_charges_fee" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2" data-demo-target="payment-method">
                                <label class="text-sm font-medium flex"
                                    :class="form.errors.network ? 'text-red-500' : 'text-gray-700'">
                                    Payment Method 
                                    <span class="text-red-700">*</span> 
                                    <tool-tip title="Payment Method" description="<span class='font-bold'>Same Day ACH</span> – Processing time: 2-3 business days | Fee: $4 per transaction (each way) <br> <span class='font-bold'>Standard ACH</span> – Processing time: 4-5 business days | Fee: $3 per transaction (each way)"></tool-tip>
                                </label>

                                <div class="mt-2 flex items-center space-x-6">
                                    <label
                                        class="relative group flex items-center space-x-2"
                                        :class="{ 'opacity-50 cursor-not-allowed': !canSelectSameDayAch }"
                                    >
                                        <input
                                            type="radio"
                                            value="1"
                                            v-model="form.network"
                                            :disabled="!canSelectSameDayAch"
                                            class="h-5 w-5 text-indigo-600 border-gray-300 focus:ring-indigo-500 disabled:opacity-50"
                                        >
                                        <span class="text-gray-700 text-sm">Same Day ACH</span>
                                        <span
                                            v-if="!canSelectSameDayAch"
                                            class="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden w-64 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg group-hover:block"
                                            role="tooltip"
                                        >
                                            Must be a RiskGuard Pro subscriber to access expedited payments.
                                        </span>
                                    </label>
                                    <label class="flex items-center space-x-2">
                                        <input type="radio" value="2" v-model="form.network"
                                            class="h-5 w-5 text-indigo-600 border-gray-300 focus:ring-indigo-500">
                                        <span class="text-gray-700 text-sm">Standard ACH</span>
                                    </label>
                                </div>

                                <p v-if="form.errors.network" class="text-red-500 text-sm mt-1">{{ form.errors.network }}</p>
                            </div>

                            <div class="sm:col-span-2" data-demo-target="phone-number">
                                <label for="phone"
                                       v-bind:class="(form.errors.phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Phone Number <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.phone)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faPhone" class="text-gray-400" />
                                    </div>
                                    <input id="phone" v-model="form.phone" type="tel" data-demo-target="phone-number"
                                           v-bind:class="(form.errors.phone)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                                    >
                                    <p v-text="form.errors.phone"
                                       class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <label for="notes"
                                       v-bind:class="(form.errors.notes)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Any Notes
                                </label>

                                <div class="mt-1">
                                    <textarea id="notes" v-model="form.notes" rows="3" data-demo-target="lease-notes" v-bind:class="(form.errors.notes)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">

                                    </textarea>
                                    <p v-text="form.errors.notes" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div>
                        <div class="mt-5" data-demo-target="one-time-charges">
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                One Time Charges
                            </h3>
                        </div>
                        <div v-for="(charge, index) in form.charges" :key="charge.key"
                             class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                            <div class="sm:col-span-2">
                                <label for="title"
                                       v-bind:class="(form.errors.title)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Type <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">

                                    <input id="title" v-model="charge.title" :disabled="charge.payment" type="text"
                                           v-bind:class="(form.errors.title)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                           :class="charge.payment ? 'bg-gray-200' : ''"
                                    >
                                    <p v-if="charge.payment"
                                       class="text-green-800 text-sm">
                                        Paid by the Tenant
                                    </p>
                                    <p v-if="form.errors[`charges.${index}.title`]"
                                       class="text-red-500 text-sm">
                                        {{ form.errors[`charges.${index}.title`] }}
                                    </p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="amount"
                                       v-bind:class="(form.errors.amount)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Amount <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">

                                    <input id="amount" v-model="charge.amount" type="number" step="0.01"
                                           v-bind:class="(form.errors.amount)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    <small class="text-gray-500 text-sm">
                                        The amount should not be less than $200.00.
                                    </small>
                                    <p v-if="form.errors[`charges.${index}.amount`]"
                                       class="text-red-500 text-sm">
                                        {{ form.errors[`charges.${index}.amount`] }}
                                    </p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="due_date"
                                       v-bind:class="(form.errors.due_date)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Due Date <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">

                                    <input id="due_date" v-model="charge.due_date" type="date"
                                           v-bind:class="(form.errors.due_date)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    <p v-if="form.errors[`charges.${index}.due_date`]"
                                       class="text-red-500 text-sm">
                                        {{ form.errors[`charges.${index}.due_date`] }}
                                    </p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <div class="inline-flex rounded-md shadow-sm mt-0 sm:mt-6" role="group">
                                    <button v-if="!charge.payment" type="button"
                                            @click="removeField(index)"
                                            class="px-4 py-2 text-sm font-medium text-gray-900 bg-red-500 hover:bg-red-700 border border-gray-200 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white">
                                        <font-awesome-icon :icon="faMinus" class="text-white" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <button type="button"
                                @click="addField"
                                class="px-4 py-2 text-sm font-medium text-white bg-indigo-500 mt-3 hover:bg-indigo-700 border border-gray-200 focus:z-10 focus:ring-2 focus:ring-blue-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white">
                            Add More
                        </button>
                    </div>
                </div>

                <!-- Terms & Conditions Section -->
                <div v-if="subscription_data.has_access && !subscription_data.is_terms_accepted" class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                    <h3 class="text-lg font-medium text-blue-800 mb-4">Terms and Conditions</h3>
                    <p class="text-sm text-blue-700 mb-4">You must accept the Terms and Conditions to use the rent payments module.</p>
                    
                    <div class="mb-4">
                        <label class="flex items-start">
                            <input 
                                type="checkbox" 
                                v-model="form.rent_payments_terms_accepted" 
                                class="mt-1 h-4 w-4 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400 border-gray-300 rounded"
                            />
                            <span class="ml-2 text-sm text-gray-700">
                                I agree to the <a target="_blank" :href="appUrl('/terms-of-service')" class="text-rent-risk-blue-400 underline">Terms and Conditions</a> for the rent payments module
                            </span>
                        </label>
                        <p v-if="form.errors.terms_and_conditions" class="text-red-500 text-sm mt-1">{{ form.errors.terms_and_conditions }}</p>
                        <p v-if="rentPaymentsTermsErrorMessage" class="text-red-500 text-sm mt-1">{{ rentPaymentsTermsErrorMessage }}</p>
                    </div>
                    
                    <div class="flex justify-end">
                        <button 
                            type="button" 
                            @click="acceptRentPaymentsTerms"
                            :disabled="!form.rent_payments_terms_accepted"
                            class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Accept Terms
                        </button>
                    </div>
                </div>

                <!-- 86bcd9hg3 (decision 18): rent payments are part of RiskGuard Pro, not sold on
                     their own. The button opens the Pro pop-up and buys right here. -->
                <div v-if="!subscription_data.has_access" class="mb-6 rounded-lg border border-[#e0cb85] bg-[#fbf6e3] p-6" data-test="rent-payments-pro-card">
                    <h3 class="text-base font-semibold text-gray-900">Rent payments are part of RiskGuard Pro</h3>
                    <p class="mt-1 text-sm text-gray-700">Upgrade your account to Pro to collect rent on every property, along with maintenance requests, leases and more.</p>
                    <ProUpgradeButton
                        :stripe-data="stripe_data"
                        :has-payment-method="Boolean(subscription_data.payment_method)"
                        :user-payment-method="subscription_data.payment_method"
                    />
                </div>

                <!-- Success Message -->
                <div v-if="subscription_data.has_access && subscription_data.is_terms_accepted" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <font-awesome-icon :icon="faCheckCircle" class="text-green-400" />
                        </div>
                        <div class="ml-3">
                            <p class="text-sm font-medium text-green-800">
                                <template v-if="subscription_data.access_via_riskguard">
                                    You have accepted the terms and this property is covered by your {{ subscription_data.is_riskguard_pro ? 'RiskGuard Pro' : 'RiskGuard' }} subscription. You can create lease information.
                                </template>
                                <template v-else>
                                    You have accepted the terms and have an active rent payments subscription. You can create lease information.
                                </template>
                            </p>
                        </div>
                    </div>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link :href="route('properties.show', property.id) + '?tab=rent-payments'"
                                      class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Cancel
                        </inertia-link>
                        <form-button 
                            :form="form" 
                            type="submit" 
                            class="ml-3" 
                            data-demo-target="save-button"
                            :disabled="!subscription_data.has_access || !subscription_data.is_terms_accepted"
                        >
                            Save
                        </form-button>
                    </div>
                </div>
            </form>
        </div>

        <div v-if="showLeaseModal">
            <TransitionRoot as="template" :show="showLeaseModal">
                <Dialog as="div" class="relative z-10" @close="showLeaseModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to renew the previous lease term?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showLeaseModal = false">Cancel</button>
                                        <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed" :disabled="isRenewing" @click="renewLeaseTerm()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
    
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'rented-properties.lease-information'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
    </tailwind-layout>
</template>

<script>
import PropertyLayout from '@/Layouts/PropertyLayout.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import { applyProratedAmount } from './proratedCharge';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    faCalendar,
    faCalendarAlt,
    faCheckCircle,
    faCreditCard,
    faDollar,
    faEnvelope,
    faGift,
    faMapMarkerAlt,
    faMinus,
    faPhone,
    faPlus
} from '@fortawesome/pro-solid-svg-icons';
import JetButton from '@/Jetstream/Button.vue'
import JetInput from '@/Jetstream/Input.vue'
import JetCheckbox from '@/Jetstream/Checkbox.vue'
import JetLabel from '@/Jetstream/Label.vue'
import JetValidationErrors from '@/Jetstream/ValidationErrors.vue'
import FormButton from '@/Shared/FormButton.vue'
import TailwindLayout from "@/Layouts/TailwindLayout.vue";
import axios from "axios";
import { faArrowsRotate, faChevronLeft } from "@fortawesome/pro-solid-svg-icons";
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'
import ToolTip from "@/Shared/ToolTip.vue";
import ProUpgradeButton from '@/Components/ProUpgradeButton.vue';

export default {
    props: {
        property: Object,
        late_charges_types: Array,
        amount_or_percentage: Array,
        one_time_charges: Array,
        subscription_data: Object,
        stripe_data: Object,
        is_riskguard_pro: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        TailwindLayout,
        PropertyLayout,
        SelectInput,
        FontAwesomeIcon,
        JetButton,
        JetInput,
        JetCheckbox,
        JetLabel,
        JetValidationErrors,
        FormButton,
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        ToolTip,
        ToolTip,
        DemoWidget,
        ProUpgradeButton,
    },
    data() {
        return {
            faArrowsRotate,
            faChevronLeft,
            faMapMarkerAlt,
            faCalendar,
            faCalendarAlt,
            faCheckCircle,
            faCreditCard,
            faDollar,
            faEnvelope,
            faGift,
            faMinus,
            faPhone,
            faPlus,
            showLeaseModal: false,
            isRenewing: false,
            property: this.property,
            form: this.$inertia.form({
                property_id: this.property.id,
                tenant_email: null,
                lease_start_date: null, // this is actual lease start date
                lease_start: null, // this date represents when the rent is started from
                lease_end: null,
                rent_date: null,
                rent: this.property.rent,
                prorated_amount: null,
                rent_due_date: null,
                rent_late_on: null,
                move_in_date: null,
                late_charges_type: null,
                late_charges_amount_or_percentage: null,
                late_charges_fee: null,
                notes: null,
                charges: [],
                network: null,
                phone: this.getPhone(),
                // Subscription related fields
                rent_payments_terms_accepted: false

            }),
            rentPaymentsTermsErrorMessage: null,
            demoSteps: getDemoSteps('rented-properties.lease-information'),
            dayOfMonthOptions: Array.from({ length: 31 }, (_, index) => String(index + 1)),
        }
    },
    watch: {
        'form.lease_start': 'calculateProratedAmount',
        'form.move_in_date': 'calculateProratedAmount',
        'form.rent': 'calculateProratedAmount',
    },
    computed: {
        phoneNumber() {
            // Use phone column only
            return this.$page.props.user?.phone || '';
        },
        canSelectSameDayAch() {
            return this.is_riskguard_pro;
        },
    },
    mounted() {
        if (!this.canSelectSameDayAch) {
            this.form.network = '2';
        }
        this.one_time_charges.forEach(charge => {
            this.form.charges.push({
                key: this.form.charges.length + 1,
                title: charge,
                amount: null,
                due_date: null,
                type: charge === 'Prorated Amount' ? 2 : 1,
            });
        });
    },
    methods: {
        getPhone() {
            // Use phone column only
            return this.$page.props.user?.phone || '';
        },
        store() {
            this.form.post(this.route('rented-property.store-lease-information'))
        },
        addField() {
            // Add a new form field
            this.form.charges.push({key: this.form.charges.length + 1, title: null, amount: null, type: 1});
        },
        removeField(index) {
            // Remove the last form field
                this.form.charges.splice(index, 1);
        },

        async calculateProratedAmount() {
            const existingCharge = this.form.charges.find(charge => charge.type === 2);
            if(existingCharge !== undefined && existingCharge.payment) {
                return;
            }
            // Validate that all required fields are filled
            if (!this.form.lease_start || !this.form.move_in_date || !this.form.rent) {
                return;
            }

            try {
                const response = await axios.post(this.route('calculate-prorated-amount'), {
                    lease_start: this.form.lease_start,
                    move_in_date: this.form.move_in_date,
                    rent: this.form.rent,
                });

                this.form.charges = applyProratedAmount(this.form.charges, response.data.prorated_amount);
            } catch (error) {
                console.error('Error calculating prorated amount:', error);
            }
        },

        renewLeaseTerm() {
            if (this.isRenewing) {
                return;
            }
            this.isRenewing = true;
            this.$inertia.post(this.route('rented-properties.lease-term.renew', this.property.rent_info.id), {},{
                onFinish: () => {
                    this.isRenewing = false;
                    this.showLeaseModal = false;
                }
            })
        },

        acceptRentPaymentsTerms() {
            if (!this.form.rent_payments_terms_accepted) return;

            // Store current scroll position
            const currentScrollY = window.scrollY;
            this.rentPaymentsTermsErrorMessage = null;

            // axios instead of $inertia.post: Inertia v2's router.post returns void
            // so network failures bubble to onunhandledrejection rather than onError.
            // Same compliance / audit-trail concern as RAC-9963.
            axios.post(route('rent-payments.update-terms-and-conditions'), {
                terms_and_conditions: this.form.rent_payments_terms_accepted
            })
                .then(() => {
                    this.subscription_data.is_terms_accepted = true;
                    this.rentPaymentsTermsErrorMessage = null;
                    this.$page.props.flash.success = 'Terms and conditions accepted successfully!';

                    this.$nextTick(() => {
                        window.scrollTo(0, currentScrollY);

                        setTimeout(() => {
                            const subscriptionSection = document.querySelector('.bg-yellow-50, .bg-green-50');
                            if (subscriptionSection) {
                                subscriptionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 100);
                    });
                })
                .catch((error) => {
                    if (error.response && error.response.data && error.response.data.errors) {
                        this.form.errors = error.response.data.errors;
                    } else if (error.request) {
                        this.rentPaymentsTermsErrorMessage = 'Network error — please check your connection and tap Accept again.';
                    } else {
                        this.rentPaymentsTermsErrorMessage = 'Failed to accept terms. Please try again.';
                    }
                });
        }
    }
}
</script>
